// The single choke point every API call goes through.
//
// When the security layer is ON (server issued a session), each call is sent as
// an AES-GCM envelope to ONE opaque path (GATEWAY_PATH). The operation and the
// plate live inside the ciphertext, and the response is encrypted too — so the
// network tab shows neither the endpoint's purpose nor the plate/prompt/results.
// Every request also carries a short-lived bearer JWT + single-use nonce +
// request-bound proof-of-work + HMAC signature (bound to the ciphertext), all
// computed in WASM.
//
// When the layer is OFF it transparently falls back to the legacy named routes
// with a ?token= param, so the frontend works against both.
//
// Control-plane failures stay plaintext so the UI can react: 402 -> paywall,
// 429 -> rate limit, 401 -> one transparent re-init + retry.

import { ApiError, buildDetailsUrl } from '../api/http';
import { ensureSession, reinitSession, type SessionState } from './session';
import { signRequest, solvePow, sha256Hex } from './signer';
import { encryptJson, decryptJson, type Envelope } from './crypto';
import { markBlocked } from './quotaState';

/** Opaque gateway path — must match the backend (security/gateway.py). */
const GATEWAY_PATH = '/api/v2/sync';

/** Readable client path -> short op code carried (encrypted) inside the envelope. */
const OP_BY_PATH: Record<string, string> = {
  '/car-data': 'cd',
  '/fast/mnt': 'mnt',
  '/car-tax': 'tax',
  '/auto24-price': 'px',
  '/latvian-reg-check': 'lv',
  '/ai/insights': 'ai',
};

export class QuotaExceededError extends ApiError {
  readonly device: string;
  readonly payContact: string;
  constructor(message: string, device: string, payContact: string) {
    super(message, 402);
    this.name = 'QuotaExceededError';
    this.device = device;
    this.payContact = payContact;
  }
}

export class RateLimitError extends ApiError {
  readonly retryAfterSec: number;
  constructor(message: string, retryAfterSec: number) {
    super(message, 429);
    this.name = 'RateLimitError';
    this.retryAfterSec = retryAfterSec;
  }
}

export interface SecureRequestOptions {
  method?: 'GET' | 'POST';
  /** Query params (excluding auth); regnr here also binds the request. */
  query?: Record<string, string>;
  /** Plate that binds the request; defaults to query.regnr. */
  regnr?: string;
  /** JSON body/payload (POST or the AI op). */
  body?: unknown;
  signal?: AbortSignal | undefined;
  timeoutMs: number;
  label: string;
  /** Token to use when the security layer is disabled server-side. */
  legacyToken?: string;
}

function randomNonce(): string {
  const bytes = new Uint8Array(16);
  crypto.getRandomValues(bytes);
  let hex = '';
  for (let i = 0; i < bytes.length; i++) hex += bytes[i]!.toString(16).padStart(2, '0');
  return hex;
}

/** Bearer + timestamp + single-use nonce + request-bound PoW + HMAC signature. */
async function signedHeaders(
  session: SessionState,
  method: string,
  pathname: string,
  bound: string,
): Promise<Record<string, string>> {
  const ts = String(Date.now());
  const nonce = randomNonce();
  const pow = await solvePow(`req:${session.sid}:${pathname}:${bound}:${ts}:${nonce}`, session.powBitsRequest);
  const canonical = [method, pathname, bound, ts, nonce, pow].join('\n');
  const sig = await signRequest(canonical, session.signingKey);
  return {
    Authorization: `Bearer ${session.accessToken}`,
    'X-AS-Timestamp': ts,
    'X-AS-Nonce': nonce,
    'X-AS-PoW': pow,
    'X-AS-Sig': sig,
  };
}

async function runFetch(url: string, init: RequestInit, timeoutMs: number, signal?: AbortSignal): Promise<Response> {
  const controller = new AbortController();
  const timeout = globalThis.setTimeout(() => controller.abort(), timeoutMs);
  const onAbort = () => controller.abort();
  signal?.addEventListener('abort', onAbort, { once: true });
  try {
    return await fetch(url, { ...init, signal: controller.signal });
  } finally {
    globalThis.clearTimeout(timeout);
    signal?.removeEventListener('abort', onAbort);
  }
}

async function parseBody(res: Response): Promise<unknown> {
  const text = await res.text();
  if (!text.trim()) return null;
  try {
    return JSON.parse(text);
  } catch {
    return { _text: text };
  }
}

function errorMessage(body: unknown, fallback: string): string {
  if (body && typeof body === 'object' && 'error' in body) {
    const value = (body as { error?: unknown }).error;
    if (typeof value === 'string' && value.trim()) return value;
  }
  return fallback;
}

function toApiError(err: unknown, label: string, signal?: AbortSignal): ApiError {
  if (err instanceof DOMException && err.name === 'AbortError') {
    if (signal?.aborted) return new ApiError(`${label}: cancelled`, null);
    return new ApiError(`${label}: request timed out`, 504);
  }
  return new ApiError(`${label}: ${err instanceof Error ? err.message : String(err)}`, null);
}

/** Turn a plaintext control-plane error (402/429) into the right exception, or null. */
function controlPlaneError(status: number, body: unknown): ApiError | null {
  if (status === 402) {
    const b = body as { device?: string; pay_contact?: string; used?: number; limit?: number; scope?: string };
    markBlocked({
      device: b.device,
      payContact: b.pay_contact,
      used: b.used,
      limit: b.limit,
      scope: b.scope === 'network' ? 'network' : 'device',
    });
    return new QuotaExceededError(errorMessage(body, 'Free scan limit reached'), b.device ?? '', b.pay_contact ?? '@autospy_group');
  }
  return null;
}

/** Perform a signed (or legacy) request and return the parsed JSON body. */
export async function secureFetchJson<T>(path: string, opts: SecureRequestOptions): Promise<T> {
  const method = opts.method ?? 'GET';
  const regnr = (opts.regnr ?? opts.query?.regnr ?? '').toString();

  for (let attempt = 0; attempt < 2; attempt++) {
    const session = await ensureSession();

    // ---- security ON: opaque encrypted gateway ----
    if (session) {
      const op = OP_BY_PATH[path];
      if (!op) throw new ApiError(`${opts.label}: no gateway op for ${path}`, null);

      const inner: Record<string, unknown> = { op };
      if (regnr) inner.regnr = regnr;
      if (opts.body !== undefined) inner.payload = opts.body;

      const envelope = await encryptJson(session.encKey, inner);
      const bound = await sha256Hex(`${envelope.n}.${envelope.d}`);
      const url = buildDetailsUrl(GATEWAY_PATH, {});
      const pathname = new URL(url).pathname;
      const headers = {
        Accept: 'application/json',
        'Content-Type': 'application/json',
        ...(await signedHeaders(session, 'POST', pathname, bound)),
      };

      let res: Response;
      try {
        res = await runFetch(url, { method: 'POST', headers, body: JSON.stringify(envelope) }, opts.timeoutMs, opts.signal);
      } catch (err) {
        throw toApiError(err, opts.label, opts.signal);
      }

      if (res.ok) {
        const respEnv = (await parseBody(res)) as Envelope | null;
        if (!respEnv || typeof respEnv !== 'object' || !('d' in respEnv)) {
          throw new ApiError(`${opts.label}: empty response`, res.status);
        }
        const decrypted = await decryptJson<{ s: number; b: unknown }>(session.encKey, respEnv);
        if (decrypted.s >= 400) {
          throw new ApiError(errorMessage(decrypted.b, `${opts.label}: HTTP ${decrypted.s}`), decrypted.s);
        }
        return decrypted.b as T;
      }

      const body = await parseBody(res);
      const cpError = controlPlaneError(res.status, body);
      if (cpError) throw cpError;
      if (res.status === 429) {
        const retry = Number(res.headers.get('Retry-After') ?? 5);
        throw new RateLimitError(errorMessage(body, 'Rate limit exceeded'), Number.isFinite(retry) ? retry : 5);
      }
      const code = (body as { code?: string })?.code;
      if (attempt === 0 && (res.status === 401 || (res.status === 409 && code === 'REPLAY'))) {
        if (res.status === 401) await reinitSession();
        continue;
      }
      throw new ApiError(errorMessage(body, `${opts.label}: HTTP ${res.status}`), res.status);
    }

    // ---- security OFF: legacy named route + token ----
    const query: Record<string, string> = { ...(opts.query ?? {}) };
    if (opts.legacyToken) query.token = opts.legacyToken;
    const url = buildDetailsUrl(path, query);
    const headers: Record<string, string> = { Accept: 'application/json' };
    const init: RequestInit = { method, headers };
    if (opts.body !== undefined) {
      headers['Content-Type'] = 'application/json';
      init.body = JSON.stringify(opts.body);
    }

    let res: Response;
    try {
      res = await runFetch(url, init, opts.timeoutMs, opts.signal);
    } catch (err) {
      throw toApiError(err, opts.label, opts.signal);
    }
    if (res.ok) {
      const body = await parseBody(res);
      if (body === null) throw new ApiError(`${opts.label}: empty response`, res.status);
      return body as T;
    }
    const body = await parseBody(res);
    const cpError = controlPlaneError(res.status, body);
    if (cpError) throw cpError;
    throw new ApiError(errorMessage(body, `${opts.label}: HTTP ${res.status}`), res.status);
  }
  throw new ApiError(`${opts.label}: request failed`, null);
}
